import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type UserType = 'customer' | 'creator';

const USER_TYPE_STORAGE_KEY = 'user_type';

export const useUserType = () => {
  const { user } = useAuth();
  
  // Initialize from localStorage if available
  const [userType, setUserType] = useState<UserType>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(USER_TYPE_STORAGE_KEY);
      if (saved === 'customer' || saved === 'creator') {
        return saved as UserType;
      }
    }
    return 'customer';
  });

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserType = async () => {
      if (!user) {
        setLoading(false);
        setInitialLoad(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', user.id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        const fetchedType = (data?.user_type as UserType) || 'customer';
        setUserType(fetchedType);
        localStorage.setItem(USER_TYPE_STORAGE_KEY, fetchedType);
      } catch (err) {
        console.error('Error fetching user type:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user type');
        // If fetch fails, we keep the initialized value (from localStorage or default)
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };

    fetchUserType();
  }, [user]);

  // Realtime subscription to reflect immediate user_type changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel('user-type-realtime');
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
      (payload) => {
        const nextType = (payload.new as any)?.user_type || (payload.old as any)?.user_type;
        if (nextType && (nextType === 'customer' || nextType === 'creator')) {
          setUserType(nextType);
          localStorage.setItem(USER_TYPE_STORAGE_KEY, nextType);
        }
      }
    );
    channel.subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user]);

  const isCreator = userType === 'creator';
  const isCustomer = userType === 'customer';

  const updateUserType = async (newType: UserType) => {
    if (!user) return false;

    try {
      // Optimistically update localStorage and state
      setUserType(newType);
      localStorage.setItem(USER_TYPE_STORAGE_KEY, newType);

      const { error } = await supabase
        .from('profiles')
        .update({ user_type: newType })
        .eq('id', user.id);

      if (error) throw error;

      return true;
    } catch (err) {
      console.error('Error updating user type:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user type');
      return false;
    }
  };

  return {
    userType,
    isCreator,
    isCustomer,
    loading,
    initialLoad,
    error,
    updateUserType,
  };
};
